/**
 * 兑换审批（与 iOS Home/HomeView.swift 的 RedemptionInfo / RedemptionButtons / RedemptionsSheet 一致）：
 * - RedemptionInfo：奖品图标 + 名称 + "30 分 · 余额 120"
 * - RedemptionButtons：婉拒 / 同意（网页版没有生物验证，同意前用 confirm() 二次确认）
 * - RedemptionsSheet：全部待审批的申请 + "已同意，待兑现"（标记已兑现）
 */
import { Gift } from "lucide-react";
import { useState } from "react";
import { confirm, run, shallowEqual, toast, useCalendar, useStore, writeEntity, type DataState, type Redemption } from "../core";
import { Button, EmptyHint, KPIcon, SectionLabel, Sheet, SheetBody, SheetHeader } from "../ui";
import "./RedemptionsSheet.css";

/** 待审批的兑换申请，新的在前；传 childId 时只看这个孩子 */
export function pendingRedemptionsOf(s: DataState, childId?: string | null): Redemption[] {
  return Object.values(s.redemptions)
    .filter((r) => r.status === "pending" && (childId == null || r.childId === childId))
    .sort((a, b) => b.requestedAt - a.requestedAt);
}

/** 已同意、还没兑现的申请，最近同意的在前 */
function approvedRedemptionsOf(s: DataState): Redemption[] {
  return Object.values(s.redemptions)
    .filter((r) => r.status === "approved")
    .sort((a, b) => (b.decidedAt ?? 0) - (a.decidedAt ?? 0));
}

export function RedemptionInfo({ redemption, balance }: { redemption: Redemption; balance: number }) {
  const icon = useStore((s) => s.rewards[redemption.rewardId]?.icon ?? "gift");
  return (
    <div className="redemption-info">
      <span className="redemption-icon">
        <KPIcon name={icon} size={20} />
      </span>
      <div>
        <strong>{redemption.rewardName}</strong>
        <small>
          <span className="kp-num">{redemption.costPoints}</span> 分 · 余额 <span className="kp-num">{balance}</span>
        </small>
      </div>
    </div>
  );
}

/** 婉拒 / 同意 */
export function RedemptionButtons({ redemption }: { redemption: Redemption }) {
  const [busy, setBusy] = useState(false);

  async function decide(approve: boolean) {
    if (approve) {
      const ok = await confirm({
        title: `同意兑换「${redemption.rewardName}」？`,
        message: `会扣除 ${redemption.costPoints} 分。`,
        confirmText: "同意",
      });
      if (!ok) return;
    }
    setBusy(true);
    await run(() => writeEntity("redemptions", "POST", `redemptions/${redemption.id}/decide`, { decision: approve ? "approve" : "reject" }), approve ? "已同意兑换" : "已婉拒");
    setBusy(false);
  }

  return (
    <div className="redemption-buttons">
      <Button variant="soft" height={44} disabled={busy} onClick={() => void decide(false)} className="redemption-reject">
        婉拒
      </Button>
      <Button variant="dark" height={44} busy={busy} onClick={() => void decide(true)}>
        同意
      </Button>
    </div>
  );
}

export function RedemptionsSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  return (
    <Sheet open={open} onClose={onClose} label="审批兑换">
      <SheetHeader title="审批兑换" onClose={onClose} />
      <RedemptionsBody />
    </Sheet>
  );
}

function RedemptionsBody() {
  const cal = useCalendar();
  const pending = useStore((s) => pendingRedemptionsOf(s), shallowEqual);
  const approved = useStore(approvedRedemptionsOf, shallowEqual);
  const children = useStore((s) => s.children);
  const [fulfilling, setFulfilling] = useState<string | null>(null);

  async function fulfill(r: Redemption) {
    setFulfilling(r.id);
    const done = await run(() => writeEntity("redemptions", "POST", `redemptions/${r.id}/fulfill`));
    setFulfilling(null);
    if (done) toast("已标记兑现");
  }

  return (
    <SheetBody gap={12}>
      {pending.length === 0 && <EmptyHint icon={Gift} text="没有待审批的申请" />}
      {pending.map((r) => {
        const child = children[r.childId];
        return (
          <div key={r.id} className="redemption-item">
            <div className="redemption-item-head">
              <span>{child?.name ?? ""}</span>
              <span>
                {cal.relativeDay(cal.dateString(r.requestedAt))} {cal.timeString(r.requestedAt)}
              </span>
            </div>
            <RedemptionInfo redemption={r} balance={child?.pointsBalance ?? 0} />
            <RedemptionButtons redemption={r} />
          </div>
        );
      })}
      {approved.length > 0 && (
        <>
          <SectionLabel>已同意，待兑现</SectionLabel>
          {approved.map((r) => (
            <div key={r.id} className="redemption-approved">
              <div>
                <strong>{r.rewardName}</strong>
                <small>
                  {children[r.childId]?.name ?? ""} · {r.costPoints} 分
                </small>
              </div>
              <Button variant="soft" height={36} busy={fulfilling === r.id} onClick={() => void fulfill(r)} className="redemption-fulfill">
                标记已兑现
              </Button>
            </div>
          ))}
        </>
      )}
    </SheetBody>
  );
}
